import { Typography } from "@material-ui/core";
import _ from "lodash";
import React from "react";

// @ts-ignore
import AmplitudeCustomConfig from "./configs/custom/Amplitude";
import MixpanelCustomConfig from "./configs/custom/Mixpanel";
import GACustomConfig from "./configs/custom/GoogleAnalytics";
import GoogleAdsRemarketingListsConfig from "./configs/custom/GoogleAdsRemarketingLists";
import FBCustomAudiencesCustomConfig from "./configs/custom/FacebookCustomAudiences";
import FBPixelCustomConfig from "./configs/custom/FacebookPixel";
import GooglePubSubCustomConfig from "./configs/custom/GooglePubSub";
import WebhooksCustomConfig from "./configs/custom/Webhooks";

import {
  ActiveCampaignIcon,
  AdrollIcon,
  AmplitudeIcon,
  AppcuesIcon,
  AutopilotHqIcon,
  AzureIcon,
  BigQueryIcon,
  BingIcon,
  BugsnagIcon,
  CastleIcon,
  ChameleonIcon,
  ClearbitIcon,
  CrazyEggIcon,
  CrispIcon,
  CustomerIOIcon,
  DriftIcon,
  DripIcon,
  ElevioIcon,
  ErrorceptionIcon,
  FacebookIcon,
  FullstoryIcon,
  GoogleAdsIcon,
  GoogleAnalyticsIcon,
  GoogleCloudPlatformIcon,
  GoogleTagManagerIcon,
  GosquaredIcon,
  HeapIcon,
  HellobarIcon,
  HotjarIcon,
  HubspotIcon,
  InspectletIcon,
  IntercomIcon,
  IterableIcon,
  KeenIoIcon,
  KissmetricsIcon,
  KlaviyoIcon,
  LeadSquaredIcon,
  LinkedinIcon,
  LivechatIcon,
  LuckyOrangeIcon,
  MixpanelIcon,
  MouseFlowIcon,
  MouseStatsIcon,
  OlarkIcon,
  PanopolyIcon,
  ParselyIcon,
  PingdomIcon,
  PinterestIcon,
  PostgresqlIcon,
  QualarooIcon,
  RedshiftIcon,
  ReplayBirdIcon,
  RollbarIcon,
  SatisMeterIcon,
  SendInBlueIcon,
  SentryIcon,
  SnapEngageIcon,
  SnowflakeIcon,
  StonlyIcon,
  TaplyticsIcon,
  TapstreamIcon,
  TwitterIcon,
  UserpilotIcon,
  VarianceIcon,
  VeroIcon,
  WebEngageIcon,
  WebhookIcon,
  WindsorIoIcon,
  WoopraIcon,
  ZendeskIcon,
} from "../components/icons/themes/logos";
import { CustomDestinationConfig } from "./configs/custom/types";

import {
  ConfigFieldEntry,
  DestinationAutocompleteMappingEntry,
  DestinationConnectionMode,
  DestinationKey,
  DestinationName,
  DestinationSlug,
  DestinationStandardMapping,
  SaaSDestinationData,
  SaaSDestinationKey,
  SaaSDestinationSlug,
  SaaSDestinationType,
  SquareLogoElement,
  WarehouseDestinationData,
  WarehouseDestinationKey,
  WarehouseDestinationSlug,
  WarehouseDestinationType,
} from "./types";

import { getPinterestStandardMapping } from "./standard-mappings/pinterest";
import { getFacebookStandardMapping } from "./standard-mappings/facebook";
import { getGoogleAdsStandardMapping } from "./standard-mappings/google-ads";
import { getAdrollStandardMapping } from "./standard-mappings/adroll";
import { getTwitterStandardMapping } from "./standard-mappings/twitter";
import { getBingStandardMapping } from "./standard-mappings/bing";
import { LoadedDestinationInfo } from "../types/global";

export const saasDestinationSlugs: SaaSDestinationSlug[] = [
  "active-campaign",
  "amplitude",
  "adroll",
  "appcues",
  "autopilothq",
  "bing-ads",
  "bugsnag",
  "castle",
  "chameleon",
  "clearbit-enrichment",
  "crazy-egg",
  "crisp",
  "customer-io",
  "drift",
  "drip",
  "elevio",
  "errorception",
  "facebook-custom-audiences",
  "facebook-pixel",
  "full-story",
  "google-ads",
  "google-ads-remarketing-lists",
  "google-tag-manager",
  "google-analytics",
  "google-pubsub",
  "go-squared",
  "heap",
  "hello-bar",
  "hotjar",
  "hubspot",
  "inspectlet",
  "intercom",
  "iterable",
  "keen",
  "kissmetrics",
  "klaviyo",
  "lead-squared",
  "linkedin-tag",
  "live-chat",
  "lucky-orange",
  "mixpanel",
  "mouseflow",
  "mousestats",
  "olark",
  "parsely",
  "pingdom",
  "pinterest-tag",
  "qualaroo",
  "replay-bird",
  "rollbar",
  "satis-meter",
  "sendinblue",
  "sentry",
  "snap-engage",
  "stonly",
  "taplytics",
  "tapstream",
  "twitter-ads",
  "userpilot",
  "variance",
  "vero",
  "web-engage",
  "webhooks",
  "windsor-io",
  "woopra",
  "zendesk",
];

export const warehouseDestinationSlugs: WarehouseDestinationSlug[] = [
  "azure",
  "bigquery",
  "panoply",
  "postgres",
  "redshift",
  "snowflake",
];

export class SaaSDestination {
  slug: SaaSDestinationSlug;
  name: string;
  key: SaaSDestinationKey;
  type: SaaSDestinationType;
  logo: string;
  logoSquare: SquareLogoElement;
  settingsDescription: string | null;
  autocompleteMapping: DestinationAutocompleteMappingEntry[];
  configFields: ConfigFieldEntry[];
  configHelp: JSX.Element | string;
  configBodyHelp: JSX.Element | string;
  eventMappingSubheader: string;
  eventMappingBodyHelp: JSX.Element | string;
  eventMappingEventName: string;
  eventMappingConfigKey: string | null;
  connectionMode: DestinationConnectionMode;
  supportsCohorts: boolean;
  supportsTrack: boolean;
  supportsBackfill: boolean;
  configName: string;
  supportsServerSide: boolean;
  standardMappingConfig: DestinationStandardMapping | null;
  getStandardMapping: (() => Promise<DestinationStandardMapping>) | null;
  hasEventMapping: boolean;
  customConfig: CustomDestinationConfig | null;
  supportsHipaa: boolean;

  constructor(props: SaaSDestinationData) {
    const {
      slug,
      name,
      type,
      key,
      logo,
      logoSquare,
      settingsDescription,
      autocompleteMapping,
      configFields,
      configHelp,
      configBodyHelp,
      eventMappingBodyHelp,
      eventMappingEventName,
      eventMappingConfigKey,
      eventMappingSubheader,
      connectionMode,
      supportsBackfill,
      supportsCohorts,
      supportsTrack,
      supportsServerSide,
      configName,
      standardMappingConfig,
      getStandardMapping,
      hasEventMapping,
      customConfig,
      supportsHipaa,
    } = props;

    this.slug = slug;
    this.name = name;
    this.type = type;
    this.key = key || name;

    this.logo = logo;
    this.logoSquare = logoSquare || null;

    this.supportsBackfill = supportsBackfill;
    this.supportsCohorts = !!supportsCohorts;
    this.supportsServerSide = supportsServerSide || supportsBackfill;
    this.supportsTrack = supportsTrack;
    this.connectionMode = connectionMode;
    this.configHelp = configHelp;
    this.configFields = configFields;
    this.customConfig = customConfig || null;
    this.configName =
      configName || _.map(this.configFields, ({ name }) => name).join(" & ");
    this.configBodyHelp = configBodyHelp || <></>;
    this.eventMappingEventName = eventMappingEventName || `${this.name} Event`;
    this.eventMappingSubheader =
      eventMappingSubheader || `Map your events to ${eventMappingEventName}s`;
    this.eventMappingBodyHelp = eventMappingBodyHelp || <></>;
    this.eventMappingConfigKey = eventMappingConfigKey || null;
    this.hasEventMapping = hasEventMapping || false;

    this.settingsDescription = settingsDescription || null;
    this.autocompleteMapping = autocompleteMapping || [];
    this.standardMappingConfig = standardMappingConfig || null;
    this.getStandardMapping = getStandardMapping || null;

    this.supportsHipaa = supportsHipaa || false;
  }

  categoryDisplay() {
    switch (this.type) {
      case "advertising":
        return "advertising";
      case "app":
        return "apps";
      case "cohorting":
        return "cohorting";
    }
  }

  configPageUrl() {
    return `/destinations/${this.categoryDisplay()}/${this.slug}`;
  }
}

export class WarehouseDestination {
  slug: WarehouseDestinationSlug;
  name: string;
  key: WarehouseDestinationKey;
  supportsTrack: boolean;
  supportsCohorts: boolean;
  supportsBackfill: boolean;
  supportsServerSide: boolean;
  logo: string;
  logoSquare: SquareLogoElement;
  type: WarehouseDestinationType;
  hasEventMapping = false;

  constructor(props: WarehouseDestinationData) {
    const {
      logo,
      key,
      name,
      supportsTrack,
      supportsCohorts,
      slug,
      logoSquare,
    } = props;

    this.slug = slug;
    this.name = name;
    this.key = key || name;
    this.supportsTrack = supportsTrack;
    this.supportsCohorts = !!supportsCohorts;
    this.supportsBackfill = true;
    this.supportsServerSide = true;
    this.type = "warehouse";
    this.logo = logo;
    this.logoSquare = logoSquare || null;
  }
}

export const warehouseDestinations: Record<
  WarehouseDestinationSlug,
  WarehouseDestination
> = {
  azure: new WarehouseDestination({
    slug: "azure",
    name: "Azure",
    key: "Azure",
    logo: "azure_logo.png",
    logoSquare: AzureIcon,
    supportsTrack: true,
  }),
  bigquery: new WarehouseDestination({
    slug: "bigquery",
    name: "BigQuery",
    key: "BigQuery",
    logo: "big_query_logo.png",
    logoSquare: BigQueryIcon,
    supportsTrack: true,
  }),
  panoply: new WarehouseDestination({
    slug: "panoply",
    name: "Panoply",
    key: "Panoply",
    logo: "panoply_logo.png",
    logoSquare: PanopolyIcon,
    supportsTrack: true,
  }),
  postgres: new WarehouseDestination({
    slug: "postgres",
    name: "Postgres",
    key: "Postgres",
    logo: "postgres_logo.png",
    logoSquare: PostgresqlIcon,
    supportsTrack: true,
  }),
  redshift: new WarehouseDestination({
    slug: "redshift",
    name: "Redshift",
    key: "Redshift",
    logo: "redshift_logo.png",
    logoSquare: RedshiftIcon,
    supportsTrack: true,
  }),
  snowflake: new WarehouseDestination({
    slug: "snowflake",
    name: "Snowflake",
    key: "Snowflake",
    logo: "snowflake_logo.png",
    logoSquare: SnowflakeIcon,
    supportsTrack: true,
  }),
};

export const saasDestinations: Record<SaaSDestinationSlug, SaaSDestination> = {
  autopilothq: new SaaSDestination({
    slug: "autopilothq",
    name: "AutopilotHQ",
    key: "AutopilotHQ",
    type: "app",
    logo: "autopilothq_logo.png",
    logoSquare: AutopilotHqIcon,
    configHelp:
      "You can find your API Key by going to Settings -> App Connections -> Segment.",
    configFields: [{ name: "API Key", key: "apiKey", required: true }],
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "server",
  }),
  "active-campaign": new SaaSDestination({
    slug: "active-campaign",
    name: "ActiveCampaign",
    key: "ActiveCampaign",
    type: "app",
    logo: "active_campaign_logo.png",
    logoSquare: ActiveCampaignIcon,
    configHelp: (
      <>
        You can find your API Key and API URL in the Developer page by clicking
        Settings &gt; Developer.
        <br />
        <br />
        You can find your Event Key under Event Tracking in the Tracking page by
        clicking Settings &gt; Tracking. From here, you can find your Account ID
        by clicking on Event Tracking API and finding "actid".
      </>
    ),
    configName: "Credentials",
    configFields: [
      { name: "API Key", key: "apiKey", required: true, sensitive: true },
      { name: "API URL", key: "apiUrl", required: true, sensitive: true },
      { name: "Account ID", key: "actId", required: true, sensitive: true },
      { name: "Event Key", key: "key", required: true, sensitive: true },
    ],
    supportsBackfill: false,
    supportsTrack: true,
    supportsServerSide: true,
    connectionMode: "server",
  }),
  adroll: new SaaSDestination({
    slug: "adroll",
    name: "AdRoll",
    key: "AdRoll",
    type: "advertising",
    hasEventMapping: true,
    logo: "adroll_logo.png",
    logoSquare: AdrollIcon,
    configBodyHelp: (
      <Typography variant="body2">
        AdRoll is a retargeting network that allows you to show ads across the
        web to visitors who previously landed on your site.
      </Typography>
    ),
    eventMappingEventName: "AdRoll Segment ID",
    eventMappingConfigKey: "_events",
    eventMappingBodyHelp: (
      <Typography variant="body2">
        Use your Freshpaint events to segment your users in AdRoll. AdRoll
        allows you to create a Segment Name and ID within AdRoll for conversion
        events. Use this mapping to trigger the AdRoll Segment ID whenever the
        Freshpaint Event occurs.
        <br />
        <br />
        Get started by creating a new segment in the AdRoll interface, copying
        the Segment ID, and pasting that following a + into the URL rule field
        (for example: “+f839c23v39”). Then map your Freshpaint event names to
        the corresponding AdRoll Segment ID.
      </Typography>
    ),
    configHelp: (
      <>
        You can find your Advertiser ID in your AdRoll dashboard by clicking the
        green or red dot in the lower-left corner. In the Javascript snippet,
        the Advertiser ID appears as adroll_avd_id = &apos;XXXXXXX&apos; on line
        2.
        <br />
        <br />
        You can find your Pixel ID in your AdRoll dashboard by clicking the
        green or red dot in the lower-left corner. In the Javascript snippet,
        the Pixel ID appears as adroll_pix_id = &apos;XXXXXXX&apos; on line 3.
      </>
    ),
    configName: "Pixel ID (Required)",
    configFields: [
      { name: "Advertiser ID", key: "advId", required: true },
      {
        name: "Pixel ID",
        key: "pixId",
        required: true,
      },
    ],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
    getStandardMapping: getAdrollStandardMapping,
  }),
  amplitude: new SaaSDestination({
    slug: "amplitude",
    name: "Amplitude",
    key: "Amplitude",
    type: "app",
    logo: "amplitude_logo.png",
    logoSquare: AmplitudeIcon,
    configHelp: "Find your API key on your project's dashboard.",
    configFields: [
      { name: "API Key", key: "apiKey", required: true },
      {
        name: "Track Referrer",
        key: "trackReferrer",
        required: false,
        type: "boolean",
        description:
          "If enabled, Amplitude will capture the user’s referrer, initial referrer, and referring domain" +
          " as user properties",
      },
      {
        name: "Force HTTPS",
        key: "forceHttps",
        required: false,
        type: "boolean",
        description:
          "If enabled, events will always be uploaded to Amplitude's HTTPS endpoint. Otherwise the SDK" +
          "will use the current visitor's protocol.",
      },
      {
        name: "Use Log Revenue API V2",
        key: "useLogRevenueV2",
        required: false,
        type: "boolean",
        description: (
          <>
            If enabled, event properties are tracked with the revenue event. For
            example, you would be able to track a certain event and attach{" "}
            <code className="text-xs">price</code> and{" "}
            <code className="text-xs">quantity</code> properties to it. This
            feature will then log total revenue{" "}
            <code className="text-xs">(price*quantity)</code>
          </>
        ),
      },
      {
        name: "Track UTM properties",
        key: "trackUtmProperties",
        required: false,
        type: "boolean",
        description:
          "If enabled, Amplitude will capture UTM properties from the URL or cookie as user properties.",
      },
      {
        name: "Track Google Click ID",
        key: "trackGclid",
        required: false,
        type: "boolean",
        description: "If enabled, Amplitude will capture the google click ID.",
      },
      {
        name: "Batch Events",
        key: "batchEvents",
        required: false,
        type: "boolean",
        description: (
          <>
            If enabled, amplitude will send events in batches of{" "}
            <span className="font-mono pt-1 ">Batch Threshold</span> or every{" "}
            <span className="font-mono pt-1 ">Batch Period</span> seconds.
          </>
        ),
      },
      {
        name: "Batch Threshold",
        key: "eventUploadThreshold",
        required: false,
        type: "number",
        description: "The number of events",
        enabledIfCheckbox: "batchEvents",
      },
      {
        name: "Batch Period",
        key: "eventUploadPeriodMillis",
        required: false,
        type: "number",
        description:
          "If enabled, Amplitude will capture UTM properties from the URL or cookie as user properties.",
        enabledIfCheckbox: "batchEvents",
      },
      {
        key: "saveParamsReferrerOncePerSession",
        name: "Save Referrer, URL Params, GCLID Once Per Session",
        required: false,
        sensitive: false,
        defaultValue: false,
        type: "boolean",
        description: (
          <>
            If enabled then Google Click ID, Referrer, and UTM params will only
            be tracked once per session. New values that come in during the
            middle of the user's session will be ignored. Disable to always
            capture new values.
          </>
        ),
      },
      {
        key: "preferAnonymousIdForDeviceId",
        name: "Use Freshpaint ID for Device ID",
        required: false,
        sensitive: false,
        defaultValue: false,
        type: "boolean",
        description: (
          <>
            Enable this setting to use the Freshpaint ID as the Amplitude device
            ID. Changing this setting will break historical data, so only enable
            this if you're setting up a new Amplitude project.
          </>
        ),
      },
    ],
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "client",
    customConfig: AmplitudeCustomConfig,
  }),
  appcues: new SaaSDestination({
    slug: "appcues",
    name: "Appcues",
    key: "Appcues",
    type: "app",
    logo: "appcues_logo.png",
    logoSquare: AppcuesIcon,
    configHelp: "Find your Account ID in the settings page.",
    configFields: [{ name: "Account ID", key: "appcuesId", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  "bing-ads": new SaaSDestination({
    slug: "bing-ads",
    name: "Bing Ads",
    key: "Bing Ads",
    type: "advertising",
    hasEventMapping: true,
    logo: "bing_logo.png",
    logoSquare: BingIcon,
    eventMappingConfigKey: "_events",
    eventMappingEventName: "Bing Standard Event",
    eventMappingBodyHelp: (
      <Typography variant="body1">
        In order for Freshpaint to map your events to a Bing Conversion Goal,
        you will first need to create the goal in your Bing Ads account.
        <br />
        <br />
        You will complete the event mapping in your Bing Ads account as part of
        creating your Conversion Goal. Make sure to add the Freshpaint event
        name as the label field and to associate the goal to the correct Tag ID
        (<b>Bing Universal Event Tracking Tag ID</b>) that is set up in
        Freshpaint.
      </Typography>
    ),
    configBodyHelp: (
      <Typography variant="body2">
        When you integrate Bing Ads in Freshpaint, Freshpaint automatically
        loads the Bing Ads script on your website right away. Events from
        Freshpaint will also be recorded in Bing Ads, so you can set up a goal
        in Bing Ads that is triggered by those events you’ve already got
        installed, rather than needing to write any new code into your site.
      </Typography>
    ),
    configHelp:
      "Add your Bing Universal Event Tracking Tag ID to create campaigns and advertise your product in Bing search.",
    configFields: [{ name: "Tag ID", key: "tagId", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
    getStandardMapping: getBingStandardMapping,
  }),
  bugsnag: new SaaSDestination({
    slug: "bugsnag",
    name: "Bugsnag",
    key: "Bugsnag",
    type: "app",
    logo: "bugsnag_logo.png",
    logoSquare: BugsnagIcon,
    configHelp: "Find your API Key in the settings page.",
    configFields: [{ name: "API Key", key: "apiKey", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  castle: new SaaSDestination({
    slug: "castle",
    name: "Castle",
    key: "Castle",
    type: "app",
    logo: "castle_logo.png",
    logoSquare: CastleIcon,
    configHelp: "Find your Publishable API Key in the settings page.",
    configFields: [
      { name: "Publishable API Key", key: "publishableKey", required: true },
    ],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  chameleon: new SaaSDestination({
    slug: "chameleon",
    name: "Chameleon",
    key: "Chameleon",
    type: "app",
    logo: "chameleon_logo.png",
    logoSquare: ChameleonIcon,
    configHelp: "Go to install via Segment and click Copy Segment API Key.",
    configFields: [{ name: "API Key", key: "apiKey", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  "clearbit-enrichment": new SaaSDestination({
    slug: "clearbit-enrichment",
    name: "Clearbit Enrichment",
    key: "Clearbit Enrichment",
    type: "app",
    logo: "empty_logo.png",
    logoSquare: ClearbitIcon,
    configHelp: (
      <>
        Find your Secret API Key (starts with 'sk_') on the{" "}
        <a href="https://dashboard.clearbit.com/api">API page</a>
      </>
    ),
    configFields: [{ name: "API Key", key: "apiKey", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "server",
  }),
  crisp: new SaaSDestination({
    slug: "crisp",
    name: "Crisp",
    key: "Crisp",
    type: "app",
    logo: "crisp_logo.png",
    logoSquare: CrispIcon,
    configName: "Settings",
    configHelp: (
      <>
        Find your Website ID at Settings &gt; Website Settings &gt; Settings
        &gt; Setup Instructions.
        <br />
        <br />
        Find your Token config by signing in to the{" "}
        <a
          style={{ color: "blue" }}
          href="https://go.crisp.chat/account/token/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Crisp token generation utility
        </a>
        .
      </>
    ),
    configFields: [
      { name: "Website ID", key: "websiteId", required: true },
      {
        name: "Token Identifier",
        key: "identifier",
        required: true,
        sensitive: true,
      },
      { name: "Token Key", key: "key", required: true, sensitive: true },
    ],
    supportsBackfill: false,
    supportsTrack: true,
    supportsServerSide: true,
    connectionMode: "client",
  }),
  "crazy-egg": new SaaSDestination({
    slug: "crazy-egg",
    name: "Crazy Egg",
    key: "Crazy Egg",
    type: "app",
    logo: "crazy_egg_logo.png",
    logoSquare: CrazyEggIcon,
    configHelp: "Find your Account Number under Account > Your Profile.",
    configFields: [
      { name: "Account Number", key: "accountNumber", required: true },
    ],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  "customer-io": new SaaSDestination({
    slug: "customer-io",
    name: "Customer.io",
    key: "Customer.io",
    type: "app",
    logo: "customer_io_logo.png",
    logoSquare: CustomerIOIcon,
    configHelp:
      "Your credentials can be found under Integrations > Customer.io API.",
    configName: "Credentials",
    configFields: [
      { name: "Site ID", key: "siteId", required: true },
      {
        name: "Secret API Key",
        key: "secretAPIKey",
        required: true,
      },
    ],
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "client",
  }),
  drift: new SaaSDestination({
    slug: "drift",
    name: "Drift",
    key: "Drift",
    type: "app",
    logo: "drift_logo.png",
    logoSquare: DriftIcon,
    configHelp: "Your token is in quotes at the end of your JS script.",
    configFields: [{ name: "Token", key: "embedId", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  drip: new SaaSDestination({
    slug: "drip",
    name: "Drip",
    key: "Drip",
    type: "app",
    logo: "drip_logo.png",
    logoSquare: DripIcon,
    configHelp: "Your token is in your JS script after '_dcs.account ='.",
    configFields: [{ name: "Token", key: "account", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  elevio: new SaaSDestination({
    slug: "elevio",
    name: "Elevio",
    key: "Elevio",
    type: "app",
    logo: "elevio_logo.png",
    logoSquare: ElevioIcon,
    configHelp: "Find your Account ID on the install page.",
    configFields: [{ name: "Account ID", key: "accountId", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  errorception: new SaaSDestination({
    slug: "errorception",
    name: "Errorception",
    key: "Errorception",
    type: "app",
    logo: "errorception_logo.png",
    logoSquare: ErrorceptionIcon,
    configHelp: "Your token is in the URL after '/projects/'.",
    configFields: [{ name: "Token", key: "projectId", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  "facebook-custom-audiences": new SaaSDestination({
    slug: "facebook-custom-audiences",
    name: "Facebook Custom Audiences",
    key: "Facebook Custom Audiences",
    type: "cohorting",
    logo: "facebook.png",
    logoSquare: FacebookIcon,
    supportsBackfill: false,
    supportsTrack: false,
    supportsCohorts: true,
    configHelp: (
      <>
        You can get your Ad Account ID by going to the{" "}
        <a href="https://business.facebook.com/settings">
          Facebook Business Settings
        </a>
        , clicking Ad Accounts, and copying your Ad Account ID at the top of the
        page.
      </>
    ),
    configFields: [
      { name: "Ad Account ID", key: "adAccountId", required: true },
      { name: "OAuth Token", key: "oauthToken", required: true },
    ],
    connectionMode: "server",
    customConfig: FBCustomAudiencesCustomConfig,
  }),
  "facebook-pixel": new SaaSDestination({
    slug: "facebook-pixel",
    name: "Facebook Pixel",
    key: "Facebook Pixel",
    type: "advertising",
    hasEventMapping: true,
    logo: "facebook_pixel_logo.png",
    logoSquare: FacebookIcon,
    configHelp: "Find your Pixel ID in the Events Manager.",
    configFields: [
      { name: "Pixel ID", key: "pixelId", required: true },
      { name: "OAuth Token", key: "oauthToken", required: false },
      {
        name: "Advanced Matching",
        key: "initWithExistingTraits",
        required: true,
        type: "boolean",
        description: (
          <>
            If true, we will initialize Facebook Pixel with any user traits that
            have been cached in the Freshpaint cookies from your previous
            .identify() calls.
          </>
        ),
      },
      {
        name: "Use UserId or Anonymous Id as External Id",
        key: "userIdAsExternalId",
        required: true,
        type: "boolean",
        description: (
          <>
            Enable this setting if you want to send userId (or anonymousId if
            not present) as external Id to Facebook. Must have "Advanced
            Matching" enabled.
          </>
        ),
      },
    ],
    configBodyHelp: (
      <Typography variant="body2">
        The Facebook Pixel integration optimizes the performance of your
        Facebook Ads. Freshpaint adds Facebook Pixel to your site and enables
        you to automatically send event data back to Facebook for more efficient
        ad spend.{" "}
        <a href="https://developers.facebook.com/docs/facebook-pixel">
          Facebook Pixel Documentation {">"}
        </a>
      </Typography>
    ),
    customConfig: FBPixelCustomConfig,
    eventMappingEventName: "Facebook Standard Event",
    eventMappingConfigKey: "_standardEvents",
    eventMappingSubheader: "Map your events to Standard FB Events",
    eventMappingBodyHelp: (
      <Typography variant="body2">
        Make your ad spend more efficient & optimized. Facebook recognizes{" "}
        <a href="https://developers.facebook.com/docs/facebook-pixel/reference#standard-events">
          certain standard events
        </a>{" "}
        that can be used across custom audiences, custom conversions, conversion
        tracking, and conversion optimization within Facebook. When you map an
        event to a standard Facebook event, we will automatically send the event
        by that name to Facebook. Unmapped events can still be sent to Facebook
        as custom events.
      </Typography>
    ),
    supportsBackfill: false,
    supportsTrack: true,
    supportsServerSide: true,
    connectionMode: "client",
    autocompleteMapping: [
      { name: "AddPaymentInfo" },
      { name: "AddToCart" },
      { name: "AddToWishlist" },
      { name: "CompleteRegistration" },
      { name: "Contact" },
      { name: "CustomizeProduct" },
      { name: "Donate" },
      { name: "FindLocation" },
      { name: "InitiateCheckout" },
      { name: "Lead" },
      { name: "PageView" },
      { name: "Purchase" },
      { name: "Schedule" },
      { name: "Search" },
      { name: "StartTrial" },
      { name: "SubmitApplication" },
      { name: "Subscribe" },
      { name: "ViewContent" },
    ],
    getStandardMapping: getFacebookStandardMapping,
  }),
  "full-story": new SaaSDestination({
    slug: "full-story",
    name: "FullStory",
    key: "FullStory",
    type: "app",
    logo: "fullstory_logo.png",
    logoSquare: FullstoryIcon,
    configHelp: "Use the token in your JS snippet to the right of '_fs_org'.",
    configFields: [{ name: "Org ID", key: "org", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  "google-ads": new SaaSDestination({
    slug: "google-ads",
    name: "Google Ads",
    key: "Google AdWords New",
    type: "advertising",
    hasEventMapping: true,
    logo: "google_ads_logo.png",
    logoSquare: GoogleAdsIcon,
    configHelp:
      "Enter your Google Conversion ID. You can get this from your global site tag snippet. It should look similar to AW-302243732.",
    configFields: [{ name: "Conversion ID", key: "accountId", required: true }],
    configBodyHelp: (
      <Typography variant="body2">
        Google is where people search for what to do, where to go, and what to
        buy. Your digital ads can appear on Google at the very moment someone is
        looking for products or services like yours.
      </Typography>
    ),
    eventMappingBodyHelp: (
      <Typography variant="body2">
        Make your Google ad spend more efficient & optimized. You can use
        conversion tracking to track when someone clicks a button or link on
        your website, or when someone who’s clicked your ad goes to your website
        and completes an action that you’ve defined as valuable, such as a
        purchase or sign-up.
      </Typography>
    ),
    eventMappingConfigKey: "_clickConversions",
    eventMappingSubheader:
      "Map your events to Click and Pageview Conversions with Google Ads",
    eventMappingEventName: "Google Conversion Pixel ID",
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
    getStandardMapping: getGoogleAdsStandardMapping,
  }),
  "google-tag-manager": new SaaSDestination({
    slug: "google-tag-manager",
    name: "Google Tag Manager",
    key: "Google Tag Manager",
    type: "app",
    hasEventMapping: false,
    logo: "google_ads_logo.png",
    logoSquare: GoogleTagManagerIcon,
    configHelp:
      "Enter your Google Conversion ID. You can get this from your global site tag snippet. It should look similar to AW-302243732.",
    configFields: [
      {
        name: "Container ID",
        key: "containerId",
        required: true,
        description: (
          <>
            Your Container ID from your{" "}
            <a href="https://tagmanager.google.com/#/management/Accounts/">
              GTag Accounts page
            </a>
            .
          </>
        ),
      },
      {
        name: "Environment",
        key: "environment",
        required: false,
        description: (
          <>
            (Optional) Your environment variable for gtm previews. IMPORTANT:
            make sure the string includes gtm_auth. For example, your string
            should look like <code>env-xx&gtm_auth=xxxxxx</code>
          </>
        ),
      },
    ],
    configBodyHelp: (
      <Typography variant="body2">
        Google Tag Manager is a{" "}
        <a href="https://en.wikipedia.org/wiki/Tag_management_system">
          tag management system (TMS)
        </a>{" "}
        that allows you to quickly and easily update measurement codes and
        related code fragments collectively known as tags on your website or
        mobile app.
        <a href="https://marketingplatform.google.com/about/tag-manager/?utm_source=segment&utm_medium=integrations-page&utm_campaign=partners">
          Google Tag Manager Website.
        </a>
      </Typography>
    ),
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  "google-ads-remarketing-lists": new SaaSDestination({
    slug: "google-ads-remarketing-lists",
    name: "Google Ads Remarketing Lists",
    key: "Google Ads Remarketing Lists",
    type: "cohorting",
    logo: "google_ads_logo.png",
    logoSquare: GoogleAdsIcon,
    supportsBackfill: false,
    supportsTrack: false,
    supportsCohorts: true,
    configHelp: (
      <>
        You can get your Customer ID by signing into your{" "}
        <a href="https://ads.google.com">Google Ads account</a>, clicking the
        help icon in the top right corner, and finding the "Customer ID" at the
        bottom of the menu.
      </>
    ),
    configFields: [
      { name: "Customer ID", key: "customerId", required: true },
      { name: "OAuth Token", key: "oauthToken", required: true },
    ],
    connectionMode: "server",
    customConfig: GoogleAdsRemarketingListsConfig,
  }),
  "google-analytics": new SaaSDestination({
    slug: "google-analytics",
    name: "Google Analytics",
    key: "Google Analytics",
    type: "app",
    logo: "google_analytics_logo.png",
    logoSquare: GoogleAnalyticsIcon,
    configHelp: "Find your tracking ID under Admin > Property Settings.",
    customConfig: GACustomConfig,
    configFields: [
      { name: "Tracking ID", key: "trackingId", required: true },
      {
        name: "Site Speed Sample Rate",
        key: "siteSpeedSampleRate",
        required: true,
        type: "number",
        description: (
          <>
            Specifies what percentage of users should be sampled for Site Speed
            metrics. If you have a small number of visitors you might want to
            adjust the sampling to a larger rate for your site speed stats.
          </>
        ),
      },
      {
        name: "Sampling Rate",
        key: "sampleRate",
        required: true,
        type: "number",
        description: (
          <>
            Specifies what percentage of users should be tracked. This defaults
            to 100 (all users are sampled) but high-traffic sites should use a
            lower sample rate to adhere to Google Analytics processing limits as{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#sampleRate"
            >
              seen here
            </a>
            .
          </>
        ),
      },
      {
        name: "Name Tracker",
        key: "nameTracker",
        required: false,
        type: "boolean",
        description: (
          <>
            Names the tracker{" "}
            <code className="text-xs">freshpaintGATracker</code> if enabled. Use
            this if you're working with additional GA trackers and want to
            ensure that your Freshpaint tracker name is distinct. If this is
            enabled you must prepend this tracker name to any native Google
            Analytics (except for <code className="text-xs">create</code>) that
            you call, e.g.
            <code className="text-xs">freshpaintGATracker.require(....)</code>
          </>
        ),
      },
      {
        name: "Enhanced Ecommerce",
        required: false,
        key: "enhancedEcommerce",
        type: "boolean",
        description: (
          <>
            If you want more detailed reports on ecommerce, you might want to
            enable this feature. Read more about it{" "}
            <a
              target="_blank"
              rel="noreferrer noopener"
              href="https://developers.google.com/analytics/devguides/collection/analyticsjs/enhanced-ecommerce"
            >
              here
            </a>
            .
          </>
        ),
      },
      {
        name: "Anonymize IP Addresses",
        required: false,
        key: "anonymizeIp",
        type: "boolean",
        description: (
          <>
            If enabled, IP addresses will be anonymized on the client side. Read
            more about anonymizing IP addresses from the{" "}
            <a
              target="_blank"
              rel="noreferrer noopener"
              href="https://support.google.com/analytics/answer/2763052?hl=en"
            >
              Google support documentation
            </a>
            .
          </>
        ),
      },
    ],
    supportsBackfill: false,
    supportsTrack: true,
    supportsServerSide: true,
    connectionMode: "client",
  }),
  "google-pubsub": new SaaSDestination({
    slug: "google-pubsub",
    name: "Google PubSub",
    key: "Google PubSub",
    type: "app",
    logo: "empty_logo.png",
    logoSquare: GoogleCloudPlatformIcon,
    configHelp: (
      <>
        Allow Freshpaint to publish events to a Pub/Sub topic on your behalf by
        adding <code>pubsub@freshpaint-1.iam.gserviceaccount.com</code> as a
        Pub/Sub publisher in your{" "}
        <a href="https://console.cloud.google.com/cloudpubsub/topicList">
          Google Cloud Console
        </a>
        .
      </>
    ),
    configFields: [
      {
        name: "Mappings",
        key: "eventTopicMappings",
        required: true,
        type: "keyValueList",
        defaultValue: [],
      },
    ],
    hasEventMapping: false,
    customConfig: GooglePubSubCustomConfig,
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "server",
  }),
  "go-squared": new SaaSDestination({
    slug: "go-squared",
    name: "GoSquared",
    key: "GoSquared",
    type: "app",
    logo: "gosquared_logo.png",
    logoSquare: GosquaredIcon,
    configHelp: "Your project token can be found under 'code free setup'.",
    configFields: [{ name: "Project Token", key: "apiSecret", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  heap: new SaaSDestination({
    slug: "heap",
    name: "Heap",
    key: "Heap",
    type: "app",
    logo: "heap_logo.png",
    logoSquare: HeapIcon,
    configHelp: "Find your App ID under account > settings > Projects.",
    configFields: [{ name: "App ID", key: "appId", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  "hello-bar": new SaaSDestination({
    slug: "hello-bar",
    name: "Hello Bar",
    key: "Hello Bar",
    type: "app",
    logo: "hello_bar_logo.png",
    logoSquare: HellobarIcon,
    configHelp: "The key is to the right of my.hellobar.com in the JS script.",
    configFields: [{ name: "API Key", key: "apiKey", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  hotjar: new SaaSDestination({
    slug: "hotjar",
    name: "Hotjar",
    key: "Hotjar",
    type: "app",
    logo: "hotjar_logo.png",
    logoSquare: HotjarIcon,
    configHelp: "Your Site ID is on the left of Settings > Sites & Orgs",
    configFields: [{ name: "App ID", key: "hjid", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  hubspot: new SaaSDestination({
    slug: "hubspot",
    name: "HubSpot",
    key: "HubSpot",
    type: "app",
    logo: "hubspot_logo.png",
    logoSquare: HubspotIcon,
    configHelp: "Find your Hub ID under the account dropdown.",
    configFields: [{ name: "Hub ID", key: "portalId", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  inspectlet: new SaaSDestination({
    slug: "inspectlet",
    name: "Inspectlet",
    key: "Inspectlet",
    type: "app",
    logo: "inspectlet_logo.png",
    logoSquare: InspectletIcon,
    configHelp: "Your WID is the big number at the start of the JS snippet.",
    configFields: [{ name: "WID", key: "wid", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  intercom: new SaaSDestination({
    slug: "intercom",
    name: "Intercom",
    key: "Intercom",
    type: "app",
    logo: "intercom_logo.png",
    logoSquare: IntercomIcon,
    configHelp: "Your App ID is the code in the URL after /apps.",
    configFields: [{ name: "accountID", key: "appId", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  iterable: new SaaSDestination({
    slug: "iterable",
    name: "Iterable",
    key: "Iterable",
    type: "app",
    logo: "iterable_logo.png",
    logoSquare: IterableIcon,
    configHelp:
      "You can create a Javascript API Key under Destinations > API Keys",
    configFields: [
      { name: "Javascript API Key", key: "apiKey", required: true },
    ],
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "server",
  }),
  keen: new SaaSDestination({
    slug: "keen",
    name: "Keen IO",
    key: "Keen IO",
    type: "app",
    logo: "keen_io_logo.png",
    logoSquare: KeenIoIcon,
    configHelp: "Find your keys under Installation > JavaScript.",
    configName: "Keys",
    configFields: [
      { name: "Project ID", key: "projectId", required: true },
      {
        name: "Write Key",
        key: "writeKey",
        required: true,
      },
    ],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  kissmetrics: new SaaSDestination({
    slug: "kissmetrics",
    name: "KISSmetrics",
    key: "KISSmetrics",
    type: "app",
    logo: "kissmetrics_logo.png",
    logoSquare: KissmetricsIcon,
    configHelp: "Find your API Key under Settings > Product Settings.",
    configFields: [{ name: "Tracking API Key", key: "apiKey", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  klaviyo: new SaaSDestination({
    slug: "klaviyo",
    name: "Klaviyo",
    key: "Klaviyo",
    type: "app",
    logo: "klaviyo_logo.png",
    logoSquare: KlaviyoIcon,
    configHelp:
      "Find your public API Key under Account > Settings > API Keys. ",
    configFields: [{ name: "Public API Key", key: "apiKey", required: true }],
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "client",
    supportsHipaa: true,
  }),
  "lead-squared": new SaaSDestination({
    slug: "lead-squared",
    name: "LeadSquared",
    key: "LeadSquared",
    type: "app",
    logo: "lead_squared_logs.png",
    logoSquare: LeadSquaredIcon,
    configName: "Settings",
    configHelp:
      "Find your keys by going to My Account > Settings > API and Webhooks.",
    configFields: [
      {
        name: "Host",
        key: "host",
        required: true,
        description: `This should be either 'api', 'api-us11', or 'api-in21'.`,
      },
      {
        name: "Access Key",
        key: "accessKey",
        required: true,
        sensitive: true,
      },
      {
        name: "Secret Key",
        key: "secretKey",
        required: true,
        sensitive: true,
      },
    ],
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "server",
    supportsHipaa: true,
  }),
  "linkedin-tag": new SaaSDestination({
    slug: "linkedin-tag",
    name: "LinkedIn Tag",
    key: "LinkedIn Insight Tag",
    type: "advertising",
    logo: "linkedin_logo.png",
    logoSquare: LinkedinIcon,
    hasEventMapping: false,
    configBodyHelp: (
      <Typography variant="body2">
        The LinkedIn Insight Tag integration in Freshpaint enables in-depth
        campaign reporting and unlocks valuable insights about your site
        visitors. This lets you track conversions, retarget website visitors,
        and unlock additional insights about LinkedIn members interacting with
        your ads.
      </Typography>
    ),
    configHelp:
      "Enter your Linkedin Data Partner ID. It is a series of numbers.",
    configFields: [{ name: "Partner ID", key: "partnerId", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  "live-chat": new SaaSDestination({
    slug: "live-chat",
    name: "LiveChat",
    key: "LiveChat",
    type: "app",
    logo: "livechat_logo.png",
    logoSquare: LivechatIcon,
    configHelp: "Your token is the number in your JS script after '.license'. ",
    configFields: [{ name: "Token", key: "license", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  "lucky-orange": new SaaSDestination({
    slug: "lucky-orange",
    name: "Lucky Orange",
    key: "Lucky Orange",
    type: "app",
    logo: "lucky_orange_logo.png",
    logoSquare: LuckyOrangeIcon,
    configHelp: "Find your Site ID on the settings page. ",
    configFields: [{ name: "Site ID", key: "siteId", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  mixpanel: new SaaSDestination({
    slug: "mixpanel",
    name: "Mixpanel",
    key: "Mixpanel",
    type: "app",
    logo: "mixpanel_logo.png",
    logoSquare: MixpanelIcon,
    configHelp: "Find your keys under your project's settings page.",
    configName: "Settings",
    configFields: [
      { name: "Token", key: "token", required: true, sensitive: true },
      {
        name: "API Secret",
        key: "secret",
        sensitive: true,
        required: true,
      },
      {
        name: "Group Identifier Trait",
        key: "groupIdentifierTrait",
        required: false,
        sensitive: false,
        description:
          'The trait Freshpaint should use as your Mixpanel "group key" in group calls.',
      },
      {
        name: "Cross Subdomain Cookie",
        description:
          "This allows the Mixpanel cookie to persist between subdomains on your application.",
        key: "crossSubdomainCookie",
        required: false,
        sensitive: false,
        type: "boolean",
      },
      {
        name: "Enable EU Endpoint",
        key: "enableEuropeanUnionEndpoint",
        required: false,
        sensitive: false,
        type: "boolean",
        description: (
          <>
            When enabled, Freshpaint will send your events to Mixpanel's{" "}
            <a
              className="text-brand-red underline"
              target="_blank"
              rel="noopener noreferrer"
              href="https://developer.mixpanel.com/docs/implement-mixpanel#section-implementing-mixpanel-in-the-european-union-eu"
            >
              EU endpoint
            </a>
            . In Mixpanel, be sure to enable Data Residency under{" "}
            <b className="text-xs">Project Settings</b> &gt;{" "}
            <b className="text-xs">Set up Mixpanel</b> &gt;{" "}
            <b className="text-xs">Data Residency.</b> Make sure you&apos;ve
            followed{" "}
            <a
              className="text-brand-red underline"
              target="_blank"
              rel="noopener noreferrer"
              href="https://help.mixpanel.com/hc/en-us/articles/360039135652-Data-Residency-in-EU"
            >
              Mixpanel&apos;s help article
            </a>{" "}
            to set up EU data.
          </>
        ),
      },
      {
        name: "Secure Cookie",
        key: "secureCookie",
        required: false,
        sensitive: false,
        type: "boolean",
        description:
          "This use a secure cookie, meaning it will only be transmitted over https",
      },
      {
        name: "Consolidate Page Calls",
        key: "consolidatedPageCalls",
        required: false,
        sensitive: false,
        defaultValue: false,
        type: "boolean",
        description: (
          <>
            This will send a <b>Loaded a Page</b> event to Mixpanel for all{" "}
            <code className="inline">page()</code> method calls.
          </>
        ),
      },
      {
        name: "Ignore DNT",
        key: "ignore_dnt",
        required: false,
        sensitive: false,
        type: "boolean",
        defaultValue: true,
        description: (
          <>
            When enabled, Mixpanel will track all events, regardless of if the
            browser has "Do Not Track" enabled.{" "}
            <a
              className="text-brand-red underline"
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.eff.org/issues/do-not-track"
            >
              Learn More About DNT
            </a>
            .
          </>
        ),
      },
    ],
    customConfig: MixpanelCustomConfig,
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "client",
  }),
  mouseflow: new SaaSDestination({
    slug: "mouseflow",
    name: "Mouseflow",
    key: "Mouseflow",
    type: "app",
    logo: "mouseflow_logo.png",
    logoSquare: MouseFlowIcon,
    configHelp: "Find your Website ID on the settings page.",
    configFields: [{ name: "Website ID", key: "apiKey", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  mousestats: new SaaSDestination({
    slug: "mousestats",
    name: "MouseStats",
    key: "MouseStats",
    type: "app",
    logo: "mousestats_logo.png",
    logoSquare: MouseStatsIcon,
    configHelp: "Find your Account ID in the top right of the tracking script.",
    configFields: [
      { name: "Account ID", key: "accountNumber", required: true },
    ],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  olark: new SaaSDestination({
    slug: "olark",
    name: "Olark",
    key: "Olark",
    type: "app",
    logo: "olark_logo.png",
    logoSquare: OlarkIcon,
    configHelp: "Your Site ID is in quotes at the end of your JS script.",
    configFields: [{ name: "Site ID", key: "siteId", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  parsely: new SaaSDestination({
    slug: "parsely",
    name: "Parsely",
    key: "Parsely",
    type: "app",
    logo: "parsely_logo.png",
    logoSquare: ParselyIcon,
    configHelp: "Enter the domain you are using with Parse.ly.",
    configFields: [{ name: "Domain", key: "apiKey", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  pingdom: new SaaSDestination({
    slug: "pingdom",
    name: "Pingdom",
    key: "Pingdom",
    type: "app",
    logo: "pingdom_logo.png",
    logoSquare: PingdomIcon,
    configHelp: "Your ID is after 'pingdom.net/pa' in your RUM JS script.",
    configFields: [{ name: "RUM ID", key: "id", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  "pinterest-tag": new SaaSDestination({
    slug: "pinterest-tag",
    name: "Pinterest Tag",
    key: "Pinterest Tag",
    type: "advertising",
    hasEventMapping: true,
    logo: "pinterest_logo.png",
    logoSquare: PinterestIcon,
    configHelp:
      "Enter your Pinterest Tag ID, found on your account's Conversions page.",
    configFields: [{ name: "Tag ID", key: "tid", required: true }],
    configBodyHelp: (
      <Typography variant="body2">
        Make your Pinterest ad spend more efficient & optimized. The Freshpaint
        Pinterest Tag integration enables you to track conversions, and build
        and optimize audiences to reach valuable customers on Pinterest. Once
        configured, Freshpaint will send valuable event data to Pinterest so you
        can target basedd on actions users have token on your site, and gather
        valuable conversion insights.{" "}
        <a href="https://developers.pinterest.com/docs/ad-tools/conversion-tag/">
          Pinterest Tag Documentation {">"}{" "}
        </a>
      </Typography>
    ),
    eventMappingBodyHelp: (
      <Typography variant="body2">
        Make your ad spend more efficient & optimized. Pinterest recognizes{" "}
        <a href="https://developers.pinterest.com/docs/ad-tools/conversion-tag/?">
          certain standard events
        </a>{" "}
        that can be used to reate audiences and in conversion reporting. When
        you map an event to a standard Pinterest event, well automatically send
        the event to Pinterest. Additional events can still be sent to Pinterest
        as Custom Events to create audiences, but they may not be available in
        conversion mapping.
      </Typography>
    ),
    eventMappingConfigKey: "_pinterestEventMapping",
    eventMappingEventName: "Standard Pinterest Event",
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
    autocompleteMapping: [
      { name: "Checkout" },
      { name: "AddToCart" },
      { name: "PageVisit" },
      { name: "Signup" },
      { name: "WatchVideo" },
      { name: "Lead" },
      { name: "Search" },
      { name: "ViewCategory" },
      { name: "Custom" },
    ],
    getStandardMapping: getPinterestStandardMapping,
  }),
  qualaroo: new SaaSDestination({
    slug: "qualaroo",
    name: "Qualaroo",
    key: "Qualaroo",
    type: "app",
    logo: "qualaroo_logo.png",
    logoSquare: QualarooIcon,
    configHelp: "There is a URL in the JS that is 'ki.js/<id>/<token>.js'.",
    configFields: [
      { name: "ID", key: "customerId", required: true },
      {
        name: "Token",
        key: "siteToken",
        required: true,
      },
    ],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  "replay-bird": new SaaSDestination({
    slug: "replay-bird",
    name: "ReplayBird",
    key: "ReplayBird",
    type: "app",
    logo: "replaybird_logo.png",
    logoSquare: ReplayBirdIcon,
    configHelp:
      "Your API key is the long string of random letters and numbers in the JS script.",
    configFields: [{ name: "API Key", key: "siteKey", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  rollbar: new SaaSDestination({
    slug: "rollbar",
    name: "Rollbar",
    key: "Rollbar",
    type: "app",
    logo: "rollbar_logo.png",
    logoSquare: RollbarIcon,
    configHelp: "Your token is at Settings > Project Access Tokens.",
    configFields: [
      { name: "Client Access Token", key: "accessToken", required: true },
    ],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  "satis-meter": new SaaSDestination({
    slug: "satis-meter",
    name: "SatisMeter",
    key: "SatisMeter",
    type: "app",
    logo: "satis_meter_logo.png",
    logoSquare: SatisMeterIcon,
    configHelp: "Your key is in quotes after 'writeKey' in your JS script.",
    configFields: [{ name: "Write Key", key: "apiKey", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  sendinblue: new SaaSDestination({
    slug: "sendinblue",
    name: "Sendinblue",
    key: "Sendinblue",
    type: "app",
    logo: "empty_logo.png",
    logoSquare: SendInBlueIcon,
    configHelp:
      "Your Client Key can be found in quotes after 'client_key' in the JS script at Automation > Settings > Tracking Code",
    configFields: [{ name: "Client Key", key: "clientKey", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    supportsServerSide: true,
    connectionMode: "client",
  }),
  sentry: new SaaSDestination({
    slug: "sentry",
    name: "Sentry",
    key: "Sentry",
    type: "app",
    logo: "sentry_logo.png",
    logoSquare: SentryIcon,
    configHelp: "Your DSN can be found at the top of the projects page.",
    configFields: [{ name: "DSN", key: "config", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  "snap-engage": new SaaSDestination({
    slug: "snap-engage",
    name: "SnapEngage",
    key: "SnapEngage",
    type: "app",
    logo: "snapengage_logo.png",
    logoSquare: SnapEngageIcon,
    configHelp: "Your Widget ID is at the bottom of the 'Get the Code' tab.",
    configFields: [{ name: "Widget ID", key: "apiKey", required: true }],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "client",
  }),
  stonly: new SaaSDestination({
    slug: "stonly",
    name: "Stonly",
    key: "Stonly",
    type: "app",
    logo: "stonly_logo.png",
    logoSquare: StonlyIcon,
    configHelp:
      "You can find your API Key by going to Team Management > Integrations.",
    configFields: [{ name: "API Key", key: "apiKey", required: true }],
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "server",
  }),
  taplytics: new SaaSDestination({
    slug: "taplytics",
    name: "Taplytics",
    key: "Taplytics",
    type: "app",
    logo: "taplytics_logo.png",
    logoSquare: TaplyticsIcon,
    configHelp: "Find your JS SDK Key in the settings page.",
    configFields: [{ name: "JS SDK Key", key: "apiKey", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  tapstream: new SaaSDestination({
    slug: "tapstream",
    name: "Tapstream",
    key: "Tapstream",
    type: "app",
    logo: "tapstream_logo.png",
    logoSquare: TapstreamIcon,
    configHelp: "Find your Account Name by clicking the dot in the toolbar.",
    configFields: [
      { name: "Account Name", key: "accountName", required: true },
    ],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  "twitter-ads": new SaaSDestination({
    slug: "twitter-ads",
    name: "Twitter Ads",
    key: "Twitter Ads",
    type: "advertising",
    hasEventMapping: true,
    logo: "twitter_ads_logo.png",
    logoSquare: TwitterIcon,
    configHelp:
      "Twitter recognizes pixel IDs. This is a pixel ID you can use that will trigger on every pageview.",
    configFields: [{ name: "Page Pixel", key: "page", required: true }],
    configBodyHelp: (
      <Typography variant="body2">
        Integrate Twitter Ads with Freshpaint to send key event data to Twitter.
        Some key use cases include targeting users similar to your top
        customers, and retargeting shopping cart abandoners on Twitter.
      </Typography>
    ),
    eventMappingBodyHelp: (
      <Typography variant="body2">
        Make your ad spend more efficient & optimized. Twitter recognizes pixel
        IDs, not custom events. Freshpaint enables you to map events in
        Freshpaint that represent a Twitter conversion to its corresponding
        Twitter pixel ID.
      </Typography>
    ),
    eventMappingEventName: "Twitter Pixel ID",
    eventMappingConfigKey: "_events",
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
    getStandardMapping: getTwitterStandardMapping,
  }),
  userpilot: new SaaSDestination({
    slug: "userpilot",
    name: "Userpilot",
    key: "Userpilot",
    type: "app",
    logo: "userpilot_logo.png",
    logoSquare: UserpilotIcon,
    configHelp: (
      <Typography variant="body2">
        You can retrieve your App Token from your{" "}
        <a href="https://run.userpilot.io/settings/installation">
          Userpilot dashboard
        </a>
        .
      </Typography>
    ),
    configFields: [
      { name: "App Token", key: "appToken", required: true, sensitive: true },
    ],
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "server",
  }),
  variance: new SaaSDestination({
    slug: "variance",
    name: "Variance",
    key: "Variance",
    type: "app",
    logo: "variance_logo.png",
    logoSquare: VarianceIcon,
    configHelp:
      "Find your credentials by going to Settings > Integrations and configuring a Freshpaint integration.",
    configFields: [
      {
        name: "Webhook URL",
        key: "webhookURL",
        required: true,
        sensitive: true,
      },
      { name: "API Key", key: "apiKey", required: true, sensitive: true },
    ],
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "server",
  }),
  vero: new SaaSDestination({
    slug: "vero",
    name: "Vero",
    key: "Vero",
    type: "app",
    logo: "vero_logo.png",
    logoSquare: VeroIcon,
    configHelp: "Your API Keys can be found in your Vero settings.",
    configFields: [
      { name: "API Key", key: "apiKey", required: true },
      { name: "Auth Token", key: "authToken", required: true, sensitive: true },
    ],
    supportsBackfill: false,
    supportsServerSide: true,
    supportsTrack: true,
    connectionMode: "client",
  }),
  "web-engage": new SaaSDestination({
    slug: "web-engage",
    name: "WebEngage",
    key: "WebEngage",
    type: "app",
    logo: "webengage_logo.png",
    logoSquare: WebEngageIcon,
    configHelp: "Your token is the value in quotes at the end of the script.",
    configFields: [{ name: "Token", key: "licenseCode", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  webhooks: new SaaSDestination({
    slug: "webhooks",
    name: "Webhooks",
    key: "Webhooks",
    type: "app",
    logo: "empty_logo.png",
    logoSquare: WebhookIcon,
    configHelp: "Configure your webhook endpoints and headers",
    configFields: [
      {
        name: "Webhooks",
        key: "webhooks",
        required: true,
        type: "list",
        defaultValue: [],
      },
    ],
    hasEventMapping: false,
    customConfig: WebhooksCustomConfig,
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "server",
  }),
  "windsor-io": new SaaSDestination({
    slug: "windsor-io",
    name: "Windsor IO",
    key: "Windsor",
    type: "app",
    logo: "windsor_logo.png",
    logoSquare: WindsorIoIcon,
    configHelp: "Enter your token from the sources page.",
    configFields: [{ name: "Token", key: "apiKey", required: true }],
    supportsBackfill: true,
    supportsTrack: true,
    connectionMode: "server",
  }),
  woopra: new SaaSDestination({
    slug: "woopra",
    name: "Woopra",
    key: "Woopra",
    type: "app",
    logo: "woopra_logo.png",
    logoSquare: WoopraIcon,
    configHelp: "Enter your domain.",
    configFields: [{ name: "Domain", key: "domain", required: true }],
    supportsBackfill: false,
    supportsTrack: true,
    connectionMode: "client",
  }),
  zendesk: new SaaSDestination({
    slug: "zendesk",
    name: "Zendesk",
    key: "Zendesk",
    type: "app",
    logo: "zendesk_logo.png",
    logoSquare: ZendeskIcon,
    configName: "Credentials",
    configHelp:
      "Enter your Zendesk subdomain and add your email. Get your API key by going to Settings > Channels > API.",
    configFields: [
      { name: "Subdomain", key: "subdomain", required: true },
      { name: "Email", key: "email", required: true },
      { name: "API Key", key: "apiKey", required: true, sensitive: true },
    ],
    supportsBackfill: false,
    supportsTrack: false,
    connectionMode: "server",
  }),
};

export type DestinationDefinition = SaaSDestination | WarehouseDestination;
export const allDestinationsData: Record<
  DestinationSlug,
  DestinationDefinition
> = _.merge({}, saasDestinations, warehouseDestinations);

// @ts-ignore
export const destinationKeyToSlugMap: Record<
  DestinationName,
  DestinationSlug
> = _.chain(allDestinationsData).keyBy("key").mapValues("slug").value();

export function destinationSupportsBackfill(key: DestinationKey) {
  const slug = destinationKeyToSlugMap[key];
  const element = allDestinationsData[slug];

  return element && element.supportsBackfill;
}

export function destinationSupportsServerSide(key: DestinationKey) {
  const slug = destinationKeyToSlugMap[key];
  const element = allDestinationsData[slug];

  return element && element.supportsServerSide;
}

export function destinationSupportsTrack(key: DestinationKey) {
  const slug = destinationKeyToSlugMap[key];
  const element = allDestinationsData[slug];

  return element && element.supportsTrack;
}

export function destinationIsWarehouse(key: DestinationKey) {
  const slug = destinationKeyToSlugMap[key];
  const element = allDestinationsData[slug];

  return element.type === "warehouse";
}

export function destinationSupportsCohorts(key: DestinationKey) {
  const slug = destinationKeyToSlugMap[key];
  const element = allDestinationsData[slug];

  return element && element.supportsCohorts;
}

export function destinationIsServerSide(key: DestinationKey) {
  return destinationSupportsServerSide(key) || destinationIsWarehouse(key);
}

export const HIPAA = "hipaa";
export const getHippaFieldsForDestinations = (destination: SaaSDestination) => [
  {
    name: "BAA in Place?",
    key: "haveBAA",
    required: false,
    sensitive: false,
    type: "boolean",
    description: `Do you have a Business Associate Agreement in place with ${destination.name}?`,
  },
];
